/**
 * `TeamPanel` — the account-level team roster (ADR-0145 Slice 2c).
 *
 * Ported from the prototype `SettingsTeam`
 * (`docs/reference/Foyer_Dashboard_CURRENT.jsx` ~8079), rebuilt at the
 * ACCOUNT level and wired to the live `/v1/users` surface (Slices 1/2a/2b):
 *   - roster list (GET /v1/users)
 *   - invite teammate (POST /v1/users)
 *   - change role / suspend / reactivate / deactivate (PATCH /v1/users/:id)
 *   - resend invite (POST /v1/users/:id/resend-invite)
 *
 * The prototype's stale `Developer` role is dropped — the shipped role set
 * is owner | admin | reviewer | viewer (ADR-0008 / ADR-0145 §1). Lifecycle
 * uses suspend/deactivate (there is no hard "remove" — the API has no
 * delete; ADR-0145 §5). owner|admin only for any mutation; the dashboard
 * hide is UX-only — the server is the real gate and 403/409s regardless.
 */

import { useCallback, useEffect, useState } from 'react';
import { MoreHorizontal, Plus, Check } from 'lucide-react';

import type { UserRole } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useToast } from '../../app/providers.js';
import { formatDisplayName, formatLocation, formatUtcDateTime } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { Card, Pill, Modal, Field, ConfirmDialog, Tooltip } from '../../components/atoms/index.js';
import {
  listUsers,
  inviteUser,
  updateUser,
  resendInvite,
  listAccountSites,
  getSiteAccess,
  setSiteAccess,
  getUserActivity,
  revokeMemberSessions,
  deleteMember,
  type UserWire,
  type UserRoleWire,
  type SiteAccessInput,
  type PickerSite,
  type ActivityEntry,
} from '../../api/users.js';
import { downloadFile } from '../guardrails/policy-export.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

type RoleTone = 'lime' | 'sky' | 'mint' | 'amber';
const ROLE_INFO: Record<UserRoleWire, { tone: RoleTone; label: string; desc: string }> = {
  owner: {
    tone: 'lime',
    label: 'Owner',
    desc: 'Billing, all permissions. Only an owner can create or modify another owner.',
  },
  admin: {
    tone: 'sky',
    label: 'Admin',
    desc: 'Approve refunds, edit guardrails, manage the team, and change billing (plans, invoices, downgrade). Cannot manage owners.',
  },
  reviewer: {
    tone: 'mint',
    label: 'Reviewer',
    desc: 'Review the human-review queue and audit log. Scope defaults to every site; owners and admins can restrict it to selected sites.',
  },
  viewer: {
    tone: 'amber',
    label: 'Viewer',
    desc: 'Read-only across the dashboard.',
  },
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; users: readonly UserWire[] }
  | { kind: 'error'; message: string };

export interface TeamPanelProps {
  /** Test override for the caller role (production resolves via auth). */
  role_override?: UserRole;
  /** Test override for the caller's own user_id. */
  self_user_id_override?: string;
}

export function TeamPanel({
  role_override,
  self_user_id_override,
}: TeamPanelProps = {}): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const toast = useToast();

  const authed_user =
    auth !== null && auth.state.kind === 'authenticated' ? auth.state.user : null;
  const role: UserRole | null =
    role_override !== undefined ? role_override : authed_user?.role ?? null;
  const self_user_id: string | null =
    self_user_id_override !== undefined
      ? self_user_id_override
      : authed_user?.user_id ?? null;
  const can_write = role === 'owner' || role === 'admin';

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [roleEdit, setRoleEdit] = useState<UserWire | null>(null);
  const [accessEdit, setAccessEdit] = useState<UserWire | null>(null);
  const [activityView, setActivityView] = useState<UserWire | null>(null);
  const [sites, setSites] = useState<readonly PickerSite[]>([]);
  const [statusConfirm, setStatusConfirm] = useState<{
    user: UserWire;
    next: 'suspended' | 'deactivated';
  } | null>(null);
  // ADR-0152 Slice C — remote sign-out confirmation (destructive).
  const [revokeConfirm, setRevokeConfirm] = useState<UserWire | null>(null);
  // ADR-0153 §4 — guarded hard-delete confirmation (never-active invite).
  const [deleteConfirm, setDeleteConfirm] = useState<UserWire | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    if (http === null) return;
    const result = await listUsers(http);
    if (result.kind === 'ok') setState({ kind: 'ready', users: result.users });
    else setState({ kind: 'error', message: result.message });
  }, [http]);

  useEffect(() => {
    let cancelled = false;
    if (http === null) return;
    void (async () => {
      const result = await listUsers(http);
      if (cancelled) return;
      if (result.kind === 'ok') setState({ kind: 'ready', users: result.users });
      else setState({ kind: 'error', message: result.message });
    })();
    return () => {
      cancelled = true;
    };
  }, [http]);

  // ADR-0147 Slice 2 — load the account's sites for the access picker
  // (owner/admin only; reviewer/viewer never see the picker).
  useEffect(() => {
    let cancelled = false;
    if (http === null || !can_write) return;
    void (async () => {
      const result = await listAccountSites(http);
      if (!cancelled && result.kind === 'ok') setSites(result.sites);
    })();
    return () => {
      cancelled = true;
    };
  }, [http, can_write]);

  // --- mutation handlers -------------------------------------------------
  const onChangeRole = useCallback(
    async (user: UserWire, next: UserRoleWire): Promise<void> => {
      if (http === null) return;
      const result = await updateUser(http, user.user_id, { role: next });
      switch (result.kind) {
        case 'ok':
          toast({ tone: 'mint', title: `${formatDisplayName(user.name)} → ${ROLE_INFO[next].label}.` });
          await reload();
          break;
        case 'forbidden':
          toast({ tone: 'coral', title: 'Permission needed for that change.' });
          break;
        case 'conflict':
          toast({ tone: 'coral', title: conflictMessage(result.reason) });
          break;
        case 'not_found':
          toast({ tone: 'coral', title: 'User not found — refresh the page.' });
          break;
        default:
          toast({ tone: 'coral', title: "Couldn't change role — try again." });
      }
    },
    [http, toast, reload],
  );

  const onChangeStatus = useCallback(
    async (user: UserWire, next: UserWire['status']): Promise<void> => {
      if (http === null) return;
      const result = await updateUser(http, user.user_id, { status: next });
      switch (result.kind) {
        case 'ok':
          toast({ tone: next === 'active' ? 'lime' : 'amber', title: statusToast(formatDisplayName(user.name), next) });
          await reload();
          break;
        case 'forbidden':
          toast({ tone: 'coral', title: 'Permission needed for that change.' });
          break;
        case 'conflict':
          toast({ tone: 'coral', title: conflictMessage(result.reason) });
          break;
        case 'not_found':
          toast({ tone: 'coral', title: 'User not found — refresh the page.' });
          break;
        default:
          toast({ tone: 'coral', title: "Couldn't update — try again." });
      }
    },
    [http, toast, reload],
  );

  const onResend = useCallback(
    async (user: UserWire): Promise<void> => {
      if (http === null) return;
      const result = await resendInvite(http, user.user_id);
      switch (result.kind) {
        case 'ok':
          toast({ tone: 'mint', title: `Invite re-sent to ${user.email}.` });
          break;
        case 'rate_limited':
          toast({ tone: 'amber', title: 'Too many invite sends today — try again later.' });
          break;
        case 'conflict':
          toast({ tone: 'coral', title: 'That user has already accepted their invite.' });
          break;
        case 'forbidden':
          toast({ tone: 'coral', title: 'Permission needed to resend invites.' });
          break;
        default:
          toast({ tone: 'coral', title: "Couldn't resend the invite — try again." });
      }
    },
    [http, toast],
  );

  // ADR-0152 Slice C — remote sign-out (force-revoke a member's sessions).
  const onRevokeSessions = useCallback(
    async (user: UserWire): Promise<void> => {
      if (http === null) return;
      const result = await revokeMemberSessions(http, user.user_id);
      switch (result.kind) {
        case 'ok':
          toast({
            tone: 'lime',
            title:
              result.revoked_count === 0
                ? `${formatDisplayName(user.name)} had no active sessions.`
                : `Signed ${formatDisplayName(user.name)} out of ${result.revoked_count} session${result.revoked_count === 1 ? '' : 's'}.`,
          });
          break;
        case 'forbidden':
          toast({
            tone: 'coral',
            title:
              result.reason === 'owner_protected'
                ? 'Only an owner can sign out another owner.'
                : 'Permission needed to sign out a member.',
          });
          break;
        case 'not_found':
          toast({ tone: 'coral', title: 'User not found — refresh the page.' });
          break;
        default:
          toast({ tone: 'coral', title: "Couldn't sign the member out — try again." });
      }
    },
    [http, toast],
  );

  // ADR-0153 §4 — guarded hard-delete of a never-active invite.
  const onDelete = useCallback(
    async (user: UserWire): Promise<void> => {
      if (http === null) return;
      const result = await deleteMember(http, user.user_id);
      switch (result.kind) {
        case 'ok':
          toast({ tone: 'mint', title: `Removed ${formatDisplayName(user.name)}.` });
          await reload();
          break;
        case 'has_history':
          toast({
            tone: 'amber',
            title: 'This member has activity history — deactivate them instead of deleting.',
          });
          break;
        case 'forbidden':
          toast({ tone: 'coral', title: 'Permission needed to remove a member.' });
          break;
        case 'not_found':
          toast({ tone: 'coral', title: 'User not found — refresh the page.' });
          break;
        default:
          toast({ tone: 'coral', title: "Couldn't remove the member — try again." });
      }
    },
    [http, toast, reload],
  );

  const onInvite = useCallback(
    async (
      email: string,
      name: string,
      invite_role: UserRoleWire,
      scope: SiteAccessInput,
    ): Promise<boolean> => {
      if (http === null) return false;
      const body: {
        email: string;
        name?: string;
        role: UserRoleWire;
        site_access?: SiteAccessInput;
      } = {
        email,
        role: invite_role,
      };
      if (name.trim().length > 0) body.name = name.trim();
      // Only reviewer/viewer can be scoped; for them, send a non-default
      // 'selected' scope (owner/admin are always all-sites — LB-A1).
      if (
        (invite_role === 'reviewer' || invite_role === 'viewer') &&
        scope.mode === 'selected'
      ) {
        body.site_access = scope;
      }
      const result = await inviteUser(http, body);
      switch (result.kind) {
        case 'ok':
          toast({
            tone: 'lime',
            title: 'Invite sent.',
            body: `${email} — ${ROLE_INFO[invite_role].label}. The invite link expires in 7 days.`,
          });
          await reload();
          return true;
        case 'conflict':
          toast({ tone: 'coral', title: 'That email is already a Sill user.' });
          return false;
        case 'forbidden':
          toast({ tone: 'coral', title: 'You cannot invite at that role.' });
          return false;
        case 'rate_limited':
          toast({ tone: 'amber', title: 'Daily invite limit reached — try again tomorrow.' });
          return false;
        case 'bad_request':
          toast({ tone: 'coral', title: 'Check the email and role and try again.' });
          return false;
        default:
          toast({ tone: 'coral', title: "Couldn't send the invite — try again." });
          return false;
      }
    },
    [http, toast, reload],
  );

  const onSetAccess = useCallback(
    async (user: UserWire, scope: SiteAccessInput): Promise<boolean> => {
      if (http === null) return false;
      const result = await setSiteAccess(http, user.user_id, scope);
      switch (result.kind) {
        case 'ok': {
          const n = result.site_ids.length;
          toast({
            tone: 'mint',
            title:
              result.mode === 'all'
                ? `${formatDisplayName(user.name)} can access all sites.`
                : `${formatDisplayName(user.name)} scoped to ${n} site${n === 1 ? '' : 's'}.`,
          });
          await reload();
          return true;
        }
        case 'forbidden':
          toast({ tone: 'coral', title: 'Permission needed for that change.' });
          return false;
        case 'conflict':
          toast({ tone: 'coral', title: 'You cannot change your own site access.' });
          return false;
        case 'not_found':
          toast({ tone: 'coral', title: 'That member no longer exists.' });
          return false;
        case 'bad_request':
          toast({
            tone: 'coral',
            title:
              result.reason === 'empty_site_selection'
                ? 'Select at least one site.'
                : result.reason === 'account_wide_role_user'
                  ? 'Owners and admins always have all-site access.'
                  : result.reason === 'invalid_site'
                    ? 'One of the selected sites is invalid.'
                    : 'Check the selection and try again.',
          });
          return false;
        default:
          toast({ tone: 'coral', title: "Couldn't update site access — try again." });
          return false;
      }
    },
    [http, toast, reload],
  );

  return (
    <section id="team">
      <Card className="p-5 sm:p-6">
        <div className="mb-5 pb-4" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>
            TEAM
          </div>
          <div style={{ fontFamily: serifFont, fontSize: 22, color: C.text, letterSpacing: -0.5, marginBottom: 4 }}>
            Members
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
            Who can access this account. Roles map to scoped permissions on the audit log and human-review queue.
          </div>
        </div>

        {state.kind === 'loading' ? (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
            Loading members…
          </div>
        ) : null}

        {state.kind === 'error' ? (
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, lineHeight: 1.5 }}>
            Couldn&apos;t load members — {state.message}
          </div>
        ) : null}

        {state.kind === 'ready' ? (
          <>
            <div className="space-y-2">
              {state.users.map((m) => (
                <MemberRow
                  key={m.user_id}
                  m={m}
                  C={C}
                  can_write={can_write}
                  is_self={m.user_id === self_user_id}
                  caller_role={role}
                  menuOpen={menuOpenId === m.user_id}
                  onToggleMenu={() =>
                    setMenuOpenId(menuOpenId === m.user_id ? null : m.user_id)
                  }
                  onCloseMenu={() => setMenuOpenId(null)}
                  onChangeRole={() => {
                    setRoleEdit(m);
                    setMenuOpenId(null);
                  }}
                  onEditAccess={() => {
                    setAccessEdit(m);
                    setMenuOpenId(null);
                  }}
                  onViewActivity={() => {
                    setActivityView(m);
                    setMenuOpenId(null);
                  }}
                  onResend={() => {
                    void onResend(m);
                    setMenuOpenId(null);
                  }}
                  onSuspend={() => {
                    setStatusConfirm({ user: m, next: 'suspended' });
                    setMenuOpenId(null);
                  }}
                  onReactivate={() => {
                    void onChangeStatus(m, 'active');
                    setMenuOpenId(null);
                  }}
                  onDeactivate={() => {
                    setStatusConfirm({ user: m, next: 'deactivated' });
                    setMenuOpenId(null);
                  }}
                  onSignOut={() => {
                    setRevokeConfirm(m);
                    setMenuOpenId(null);
                  }}
                  onDelete={() => {
                    setDeleteConfirm(m);
                    setMenuOpenId(null);
                  }}
                />
              ))}
            </div>

            {can_write ? (
              <div className="pt-4">
                <button
                  type="button"
                  onClick={() => setInviteOpen(true)}
                  className="flex items-center gap-2 px-4 py-2"
                  style={{
                    background: 'transparent',
                    color: C.text,
                    border: `1px solid ${C.border}`,
                    fontFamily: sansFont,
                    fontSize: 13,
                    fontWeight: 500,
                    borderRadius: 2,
                  }}
                >
                  <Plus size={12} /> Invite teammate
                </button>
              </div>
            ) : null}
          </>
        ) : null}
      </Card>

      {can_write ? (
        <>
          <InviteModal
            open={inviteOpen}
            onClose={() => setInviteOpen(false)}
            caller_role={role}
            sites={sites}
            onInvite={onInvite}
          />
          <AccessModal
            member={accessEdit}
            sites={sites}
            onClose={() => setAccessEdit(null)}
            onSave={onSetAccess}
          />
          <ActivityModal
            member={activityView}
            onClose={() => setActivityView(null)}
          />
          <ChangeRoleModal
            member={roleEdit}
            caller_role={role}
            onClose={() => setRoleEdit(null)}
            onSave={(next) => {
              const target = roleEdit;
              setRoleEdit(null);
              if (target) void onChangeRole(target, next);
            }}
          />
          <ConfirmDialog
            open={statusConfirm !== null}
            onClose={() => setStatusConfirm(null)}
            title={
              statusConfirm?.next === 'deactivated'
                ? `Deactivate ${statusConfirm ? formatDisplayName(statusConfirm.user.name) : ''}?`
                : `Suspend ${statusConfirm ? formatDisplayName(statusConfirm.user.name) : ''}?`
            }
            body={
              statusConfirm?.next === 'deactivated'
                ? 'Deactivating cuts off access and ends their sessions. You can reactivate them later from this menu.'
                : 'Suspending cuts off access immediately. You can reactivate them later.'
            }
            consequences={
              statusConfirm?.next === 'deactivated'
                ? [
                    'Access is revoked; active sessions terminate within 5 minutes',
                    'Audit log entries remain attributed to them',
                    'Per-site role overrides are preserved for the record',
                  ]
                : [
                    'Access is revoked; active sessions terminate within 5 minutes',
                    'Per-site role overrides are preserved',
                    'Reactivate restores access where they left off',
                  ]
            }
            confirmLabel={statusConfirm?.next === 'deactivated' ? 'Deactivate' : 'Suspend'}
            confirmTone="coral"
            onConfirm={() => {
              const sc = statusConfirm;
              setStatusConfirm(null);
              if (sc) void onChangeStatus(sc.user, sc.next);
            }}
          />
          <ConfirmDialog
            open={revokeConfirm !== null}
            onClose={() => setRevokeConfirm(null)}
            title={`Sign ${revokeConfirm ? formatDisplayName(revokeConfirm.name) : ''} out everywhere?`}
            body="Force-revokes all of this member's active sessions across every device. They can sign back in with a new magic link — this does not suspend or remove them."
            consequences={[
              'Every current session for this member is revoked immediately',
              'They can request a new magic link and sign back in',
              'Recorded on the audit trail as a remote sign-out',
            ]}
            confirmLabel="Sign out everywhere"
            confirmTone="coral"
            onConfirm={() => {
              const target = revokeConfirm;
              setRevokeConfirm(null);
              if (target) void onRevokeSessions(target);
            }}
          />
          <ConfirmDialog
            open={deleteConfirm !== null}
            onClose={() => setDeleteConfirm(null)}
            title={`Delete ${deleteConfirm ? formatDisplayName(deleteConfirm.name) : ''}?`}
            body="Permanently removes this pending invite and frees the email to be invited again. Only works for someone who never accepted and has no activity — anyone with history must be deactivated instead."
            consequences={[
              'The roster row is removed and the email is freed for re-invite',
              'Any outstanding invite link is invalidated',
              'Blocked (with a prompt to deactivate) if the member has any history',
            ]}
            confirmLabel="Delete invite"
            confirmTone="coral"
            onConfirm={() => {
              const target = deleteConfirm;
              setDeleteConfirm(null);
              if (target) void onDelete(target);
            }}
          />
        </>
      ) : null}
    </section>
  );
}

interface MemberRowProps {
  m: UserWire;
  C: ReturnType<typeof useC>;
  can_write: boolean;
  is_self: boolean;
  caller_role: UserRole | null;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
  onChangeRole: () => void;
  onEditAccess: () => void;
  onViewActivity: () => void;
  onResend: () => void;
  onSuspend: () => void;
  onReactivate: () => void;
  onDeactivate: () => void;
  onSignOut: () => void;
  onDelete: () => void;
}

function MemberRow(props: MemberRowProps): JSX.Element {
  const { m, C, can_write, is_self, caller_role, menuOpen } = props;
  const info = ROLE_INFO[m.role];
  const pending = m.status === 'invited';
  const avatar = (m.name.trim()[0] ?? m.email[0] ?? '?').toUpperCase();

  // Defense-in-depth UX gating (server is the real gate): hide the menu
  // for the caller's own row (self-change refused) and for an owner row
  // when the caller is not an owner (only an owner manages owners).
  const owner_locked = m.role === 'owner' && caller_role !== 'owner';
  // MUTATION items (change role, suspend, etc.) keep the self / owner-locked
  // guard. But "View activity" is READ-ONLY (owner/admin-gated by the API),
  // so it must be reachable on EVERY row — incl. the owner's own row and the
  // owner row viewed by an admin — otherwise the owner has no visible audit
  // trail. So the menu itself shows for any owner/admin caller (can_write).
  const canMutate = can_write && !is_self && !owner_locked;
  const showMenu = can_write;
  // ADR-0147 — access scoping applies only to reviewer/viewer (owner/admin
  // are account-wide). Show a scope chip + the "Edit access" affordance for
  // those roles. `site_access` is present on the list rows.
  const scopable = m.role === 'reviewer' || m.role === 'viewer';
  const access = m.site_access;
  const accessLabel =
    !scopable || access === undefined || access.mode === 'all'
      ? null
      : `${access.site_count} site${access.site_count === 1 ? '' : 's'}`;

  return (
    <div
      className="flex items-center gap-3 p-3"
      style={{
        background: C.surface2,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        opacity: m.status === 'deactivated' ? 0.55 : pending ? 0.8 : 1,
      }}
    >
      <div
        className="flex items-center justify-center flex-shrink-0"
        style={{
          width: 32,
          height: 32,
          background: pending ? C.surface3 ?? C.surface : C.lime,
          color: pending ? C.muted : C.ink,
          fontFamily: sansFont,
          fontSize: 13,
          fontWeight: 600,
          borderRadius: 2,
        }}
      >
        {avatar}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
            {formatDisplayName(m.name)}
          </span>
          {is_self ? <Pill tone="neutral">YOU</Pill> : null}
          {pending ? <Pill tone="amber">PENDING</Pill> : null}
          {m.status === 'suspended' ? <Pill tone="coral">SUSPENDED</Pill> : null}
          {m.status === 'deactivated' ? <Pill tone="neutral">DEACTIVATED</Pill> : null}
        </div>
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginTop: 2 }}>
          {m.email}
        </div>
      </div>
      <Pill tone={info.tone}>{info.label.toUpperCase()}</Pill>
      {accessLabel !== null ? (
        <Tooltip content="This member is scoped to selected sites">
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            letterSpacing: 1,
            color: C.muted,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            padding: '2px 6px',
            whiteSpace: 'nowrap',
          }}
        >
          {accessLabel.toUpperCase()}
        </span>
        </Tooltip>
      ) : null}
      {showMenu ? (
        <div className="relative flex-shrink-0">
          <button
            type="button"
            aria-label={`Actions for ${formatDisplayName(m.name)}`}
            onClick={props.onToggleMenu}
            className="p-1.5"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
          >
            <MoreHorizontal size={13} color={C.muted} />
          </button>
          {menuOpen ? (
            <div
              className="absolute right-0 top-full mt-1 z-30"
              style={{
                width: 180,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
              }}
            >
              {/* Read-only — always available to an owner/admin caller. */}
              <MenuItem C={C} onClick={props.onViewActivity}>
                View activity
              </MenuItem>
              {canMutate && m.status !== 'deactivated' ? (
                <MenuItem C={C} onClick={props.onChangeRole}>
                  Change role
                </MenuItem>
              ) : null}
              {canMutate && m.status !== 'deactivated' && scopable ? (
                <MenuItem C={C} onClick={props.onEditAccess}>
                  Edit site access
                </MenuItem>
              ) : null}
              {canMutate && m.status === 'invited' ? (
                <MenuItem C={C} onClick={props.onResend}>
                  Resend invite
                </MenuItem>
              ) : null}
              {canMutate && m.status === 'active' ? (
                <MenuItem C={C} onClick={props.onSuspend}>
                  Suspend
                </MenuItem>
              ) : null}
              {canMutate && (m.status === 'suspended' || m.status === 'deactivated') ? (
                <MenuItem C={C} onClick={props.onReactivate}>
                  Reactivate
                </MenuItem>
              ) : null}
              {canMutate && m.status === 'active' ? (
                <MenuItem C={C} onClick={props.onSignOut}>
                  Sign out everywhere
                </MenuItem>
              ) : null}
              {canMutate && m.status !== 'deactivated' ? (
                <MenuItem C={C} danger onClick={props.onDeactivate}>
                  Deactivate
                </MenuItem>
              ) : null}
              {canMutate && (m.status === 'invited' || m.status === 'deactivated') ? (
                <MenuItem C={C} danger onClick={props.onDelete}>
                  Delete…
                </MenuItem>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({
  C,
  children,
  onClick,
  danger,
}: {
  C: ReturnType<typeof useC>;
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-3 py-2"
      style={{
        fontFamily: sansFont,
        fontSize: 12,
        color: danger ? C.coralFg : C.text,
        background: 'transparent',
        border: 'none',
        borderBottom: `1px solid ${C.borderSoft ?? C.border}`,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function invitableRoles(caller_role: UserRole | null): UserRoleWire[] {
  // LB-4: an admin cannot invite an owner. Owner can invite any role.
  return caller_role === 'owner'
    ? ['owner', 'admin', 'reviewer', 'viewer']
    : ['admin', 'reviewer', 'viewer'];
}

function InviteModal({
  open,
  onClose,
  caller_role,
  sites,
  onInvite,
}: {
  open: boolean;
  onClose: () => void;
  caller_role: UserRole | null;
  sites: readonly PickerSite[];
  onInvite: (
    email: string,
    name: string,
    role: UserRoleWire,
    scope: SiteAccessInput,
  ) => Promise<boolean>;
}): JSX.Element {
  const C = useC();
  const options = invitableRoles(caller_role);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<UserRoleWire>('viewer');
  const [accessMode, setAccessMode] = useState<'all' | 'selected'>('all');
  const [accessSiteIds, setAccessSiteIds] = useState<readonly string[]>([]);
  const [pending, setPending] = useState(false);
  // Dirty guard — Escape prompts "Discard changes?" if any invite
  // field diverges from its initial value.
  const dirty =
    email.length > 0
    || name.length > 0
    || role !== 'viewer'
    || accessMode !== 'all'
    || accessSiteIds.length > 0;

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const scopable = role === 'reviewer' || role === 'viewer';
  // A 'selected' scope must name ≥1 site (LB-A2 mirror) to submit.
  const scopeValid =
    !scopable || accessMode === 'all' || accessSiteIds.length > 0;

  const close = (): void => {
    setEmail('');
    setName('');
    setRole('viewer');
    setAccessMode('all');
    setAccessSiteIds([]);
    setPending(false);
    onClose();
  };

  const submit = async (): Promise<void> => {
    if (!validEmail || !scopeValid || pending) return;
    setPending(true);
    const scope: SiteAccessInput =
      scopable && accessMode === 'selected'
        ? { mode: 'selected', site_ids: [...accessSiteIds] }
        : { mode: 'all' };
    const ok = await onInvite(email.trim(), name, role, scope);
    setPending(false);
    if (ok) close();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="md"
      title="Invite teammate"
      subtitle="They'll get an email with a sign-in link."
      closeOnBackdrop={false}
      dirty={dirty}
    >
      <div className="p-6 space-y-4">
        <Field label="Email address" required>
          <input
            aria-label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="alex@yourcompany.com"
            className="w-full px-3 py-2.5 outline-none"
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.text,
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          />
        </Field>
        <Field label="Name" hint="Optional — defaults to the email handle.">
          <input
            aria-label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Alex Wei"
            className="w-full px-3 py-2.5 outline-none"
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.text,
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          />
        </Field>
        <Field label="Role">
          <div className="space-y-2">
            {options.map((k) => {
              const sel = role === k;
              const info = ROLE_INFO[k];
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setRole(k)}
                  className="w-full text-left p-3 flex items-start gap-3"
                  style={{
                    background: sel ? C.surface2 : C.surface,
                    border: `1px solid ${sel ? C[`${info.tone}Fg`] ?? C.border : C.border}`,
                    borderRadius: 2,
                  }}
                >
                  <div
                    className="flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 8,
                      border: `2px solid ${sel ? C[`${info.tone}Fg`] ?? C.border : C.border}`,
                      background: sel ? C[`${info.tone}Fg`] ?? 'transparent' : 'transparent',
                    }}
                  >
                    {sel ? <Check size={9} color={C.ink} /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
                        {info.label}
                      </span>
                      <Pill tone={info.tone}>{info.label.toUpperCase()}</Pill>
                    </div>
                    <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                      {info.desc}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </Field>
        {scopable ? (
          <Field
            label="Site access"
            hint="Owners and admins always reach every site; reviewers and viewers can be scoped."
          >
            <SiteAccessPicker
              C={C}
              sites={sites}
              mode={accessMode}
              site_ids={accessSiteIds}
              onChange={(mode, ids) => {
                setAccessMode(mode);
                setAccessSiteIds(ids);
              }}
            />
          </Field>
        ) : null}
      </div>
      <div
        className="px-6 py-4 flex justify-end gap-2"
        style={{ borderTop: `1px solid ${C.borderSoft ?? C.border}`, background: C.surface }}
      >
        <button
          type="button"
          onClick={close}
          className="px-4 py-2"
          style={{
            background: 'transparent',
            color: C.muted,
            border: `1px solid ${C.border}`,
            fontFamily: sansFont,
            fontSize: 13,
            borderRadius: 2,
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!validEmail || !scopeValid || pending}
          className="px-4 py-2"
          style={{
            background: C.lime,
            color: C.ink,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
            opacity: !validEmail || !scopeValid || pending ? 0.5 : 1,
            cursor: !validEmail || !scopeValid || pending ? 'not-allowed' : 'pointer',
          }}
        >
          {pending ? 'Sending…' : 'Send invite'}
        </button>
      </div>
    </Modal>
  );
}

function ChangeRoleModal({
  member,
  caller_role,
  onClose,
  onSave,
}: {
  member: UserWire | null;
  caller_role: UserRole | null;
  onClose: () => void;
  onSave: (role: UserRoleWire) => void;
}): JSX.Element {
  const C = useC();
  // Reuse the same role set as invite (owner only assignable by an owner).
  const options = invitableRoles(caller_role);
  const [role, setRole] = useState<UserRoleWire>('viewer');

  // Sync the selected role to the member when the modal opens.
  useEffect(() => {
    if (member) setRole(member.role);
  }, [member]);

  return (
    <Modal
      open={member !== null}
      onClose={onClose}
      size="md"
      title={member ? `Change ${formatDisplayName(member.name)}'s role` : 'Change role'}
      subtitle="Permission changes take effect immediately."
      closeOnBackdrop={false}
    >
      <div className="p-6 space-y-2">
        {options.map((k) => {
          const sel = role === k;
          const info = ROLE_INFO[k];
          return (
            <button
              key={k}
              type="button"
              onClick={() => setRole(k)}
              className="w-full text-left p-3 flex items-start gap-3"
              style={{
                background: sel ? C.surface2 : C.surface,
                border: `1px solid ${sel ? C[`${info.tone}Fg`] ?? C.border : C.border}`,
                borderRadius: 2,
              }}
            >
              <div
                className="flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 8,
                  border: `2px solid ${sel ? C[`${info.tone}Fg`] ?? C.border : C.border}`,
                  background: sel ? C[`${info.tone}Fg`] ?? 'transparent' : 'transparent',
                }}
              >
                {sel ? <Check size={9} color={C.ink} /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
                    {info.label}
                  </span>
                  <Pill tone={info.tone}>{info.label.toUpperCase()}</Pill>
                </div>
                <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                  {info.desc}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <div
        className="px-6 py-4 flex justify-end gap-2"
        style={{ borderTop: `1px solid ${C.borderSoft ?? C.border}`, background: C.surface }}
      >
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2"
          style={{
            background: 'transparent',
            color: C.muted,
            border: `1px solid ${C.border}`,
            fontFamily: sansFont,
            fontSize: 13,
            borderRadius: 2,
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSave(role)}
          disabled={member !== null && role === member.role}
          className="px-4 py-2"
          style={{
            background: C.lime,
            color: C.ink,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
            opacity: member !== null && role === member.role ? 0.5 : 1,
            cursor: member !== null && role === member.role ? 'not-allowed' : 'pointer',
          }}
        >
          Save role
        </button>
      </div>
    </Modal>
  );
}

function conflictMessage(reason?: string): string {
  switch (reason) {
    case 'last_owner_protected':
      return 'This is the last owner — promote another owner first.';
    case 'self_status_change_refused':
      return 'You cannot change your own status. Ask another owner or admin.';
    case 'self_role_change_refused':
      return 'You cannot change your own role. Ask another owner or admin.';
    default:
      return "Couldn't apply that change.";
  }
}

function statusToast(name: string, next: UserWire['status']): string {
  switch (next) {
    case 'active':
      return `${name} reactivated.`;
    case 'suspended':
      return `${name} suspended.`;
    case 'deactivated':
      return `${name} deactivated.`;
    default:
      return `${name} updated.`;
  }
}

// ─── ADR-0147 Slice 2 — per-site access picker + edit modal ─────────────

function SiteAccessPicker({
  C,
  sites,
  mode,
  site_ids,
  onChange,
}: {
  C: ReturnType<typeof useC>;
  sites: readonly PickerSite[];
  mode: 'all' | 'selected';
  site_ids: readonly string[];
  onChange: (mode: 'all' | 'selected', site_ids: readonly string[]) => void;
}): JSX.Element {
  const selected = new Set(site_ids);
  const toggle = (id: string): void => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange('selected', [...next]);
  };
  return (
    <div className="space-y-2">
      {(['all', 'selected'] as const).map((m) => {
        const sel = mode === m;
        const label = m === 'all' ? 'All sites' : 'Selected sites';
        const desc =
          m === 'all'
            ? 'Can reach every site in the account.'
            : 'Can reach only the sites you choose below.';
        return (
          <button
            key={m}
            type="button"
            onClick={() => onChange(m, m === 'all' ? [] : site_ids)}
            className="w-full text-left p-3 flex items-start gap-3"
            style={{
              background: sel ? C.surface2 : C.surface,
              border: `1px solid ${sel ? C.limeFg ?? C.border : C.border}`,
              borderRadius: 2,
            }}
          >
            <div
              className="flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{
                width: 16,
                height: 16,
                borderRadius: 8,
                border: `2px solid ${sel ? C.limeFg ?? C.border : C.border}`,
                background: sel ? C.limeFg ?? 'transparent' : 'transparent',
              }}
            >
              {sel ? <Check size={9} color={C.ink} /> : null}
            </div>
            <div className="flex-1 min-w-0">
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
                {label}
              </div>
              <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                {desc}
              </div>
            </div>
          </button>
        );
      })}
      {mode === 'selected' ? (
        <div
          style={{
            maxHeight: 200,
            overflowY: 'auto',
            border: `1px solid ${C.border}`,
            borderRadius: 2,
          }}
        >
          {sites.length === 0 ? (
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, padding: 12 }}>
              No sites yet.
            </div>
          ) : (
            sites.map((s) => {
              const on = selected.has(s.site_id);
              return (
                <button
                  key={s.site_id}
                  type="button"
                  onClick={() => toggle(s.site_id)}
                  className="w-full text-left px-3 py-2 flex items-center gap-3"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `1px solid ${C.borderSoft ?? C.border}`,
                    cursor: 'pointer',
                  }}
                >
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 2,
                      border: `1px solid ${on ? C.limeFg ?? C.border : C.border}`,
                      background: on ? C.limeFg ?? 'transparent' : 'transparent',
                    }}
                  >
                    {on ? <Check size={9} color={C.ink} /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text }}>
                      {s.name.trim().length > 0 ? s.name : s.domain}
                    </div>
                    <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}>
                      {s.domain}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}

function AccessModal({
  member,
  sites,
  onClose,
  onSave,
}: {
  member: UserWire | null;
  sites: readonly PickerSite[];
  onClose: () => void;
  onSave: (user: UserWire, scope: SiteAccessInput) => Promise<boolean>;
}): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const [mode, setMode] = useState<'all' | 'selected'>('all');
  const [siteIds, setSiteIds] = useState<readonly string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pending, setPending] = useState(false);

  // Load the member's current scope each time the modal opens for a member.
  useEffect(() => {
    if (member === null || http === null) return;
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    void (async () => {
      const r = await getSiteAccess(http, member.user_id);
      if (cancelled) return;
      if (r.kind === 'ok') {
        setMode(r.mode);
        setSiteIds(r.site_ids);
      } else {
        // Do NOT default a FAILED load to the most-permissive 'all' with a
        // live Save — that would let a transient error widen a member to
        // every site on one click (QA Finding 5). Surface the error and
        // disable Save until the load succeeds.
        setLoadFailed(true);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [member, http]);

  const valid = !loadFailed && (mode === 'all' || siteIds.length > 0);

  const submit = async (): Promise<void> => {
    if (member === null || !valid || pending) return;
    setPending(true);
    const scope: SiteAccessInput =
      mode === 'selected'
        ? { mode: 'selected', site_ids: [...siteIds] }
        : { mode: 'all' };
    const ok = await onSave(member, scope);
    setPending(false);
    if (ok) onClose();
  };

  return (
    <Modal
      open={member !== null}
      onClose={onClose}
      size="md"
      title="Site access"
      subtitle={member ? formatDisplayName(member.name) : ''}
      closeOnBackdrop={false}
    >
      <div className="p-6">
        {loading ? (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
            Loading…
          </div>
        ) : loadFailed ? (
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, lineHeight: 1.5 }}>
            Couldn&apos;t load this member&apos;s site access. Close and try again.
          </div>
        ) : (
          <SiteAccessPicker
            C={C}
            sites={sites}
            mode={mode}
            site_ids={siteIds}
            onChange={(m, ids) => {
              setMode(m);
              setSiteIds(ids);
            }}
          />
        )}
      </div>
      <div
        className="px-6 py-4 flex justify-end gap-2"
        style={{ borderTop: `1px solid ${C.borderSoft ?? C.border}`, background: C.surface }}
      >
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2"
          style={{
            background: 'transparent',
            color: C.muted,
            border: `1px solid ${C.border}`,
            fontFamily: sansFont,
            fontSize: 13,
            borderRadius: 2,
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!valid || pending || loading}
          className="px-4 py-2"
          style={{
            background: C.lime,
            color: C.ink,
            fontFamily: sansFont,
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
            opacity: !valid || pending || loading ? 0.5 : 1,
            cursor: !valid || pending || loading ? 'not-allowed' : 'pointer',
          }}
        >
          {pending ? 'Saving…' : 'Save access'}
        </button>
      </div>
    </Modal>
  );
}

// ─── ADR-0149 — per-member activity log (read-only) ─────────────────────

const ACTION_LABELS: Record<string, string> = {
  // ADR-0216 — catch-all row for a change made during a Sill support session.
  support_change: 'Change made by Sill support',
  user_invited: 'Invited a teammate',
  user_invite_resent: 'Resent an invite',
  user_role_changed: 'Changed a member’s role',
  user_status_changed: 'Changed a member’s status',
  user_site_role_set: 'Set a per-site role',
  user_site_role_removed: 'Removed a per-site role',
  user_site_access_set: 'Changed a member’s site access',
  account_name_change: 'Renamed the account',
  policy_imported_and_published: 'Published a policy',
  agent_card_profile_apply: 'Updated the agent card',
  buyer_detail_view: 'Viewed buyer details',
  buyer_detail_purge: 'Purged buyer details',
  buyer_detail_retention_setting_change: 'Changed buyer-data retention',
  escalation_buyer_detail_view: 'Viewed escalation buyer details',
  escalation_preflight_quote: 'Ran a preflight quote',
  // ADR-0151 — day-to-day resource actions.
  red_team_run: 'Ran the red team',
  site_settings_change: 'Changed site settings',
  guardrail_draft_saved: 'Edited guardrail rules',
  guardrail_draft_discarded: 'Discarded guardrail edits',
  // ADR-0151 Slice B — auth/session events.
  user_signed_in: 'Signed in',
  user_signed_out: 'Signed out',
  // ADR-0152 — round-2 coverage.
  escalation_resolved: 'Resolved an escalation',
  skill_toggled: 'Toggled a skill',
  site_created: 'Created a site',
  site_deleted: 'Deleted a site',
  allowed_origins_changed: 'Changed allowed origins',
  site_key_rotated: 'Rotated a site key',
  // ADR-0231 — install report-signing keys.
  install_key_issued: 'Issued an install key',
  install_key_revoked: 'Revoked an install key',
  install_revoked: 'Revoked an install',
  // ADR-0235 — "This is wrong" on a trust label.
  trust_dispute_created: 'Reported a wrong trust label',
  connector_connected: 'Connected a payment connector',
  connector_disconnected: 'Disconnected a connector',
  settlement_rail_changed: 'Changed the settlement rail',
  member_signed_out: 'Signed out a member remotely',
  activity_exported: 'Exported the activity log',
  user_deleted: 'Deleted a pending invite',
  // ADR-0243 — per-human visitors.
  visitor_ip_view: 'Viewed a visitor’s IP address',
  visitor_forgotten: 'Forgot a visitor',
  visitor_capture_setting_change: 'Changed returning-visitor tracking',
};

function labelForAction(action: string): string {
  return ACTION_LABELS[action] ?? action.replace(/_/g, ' ');
}

/** "San Francisco, US" / "United States" / '' (ADR-0150). */

/** A compact, PII-free detail line for the recognizable context shapes. */
function detailForEntry(ctx: Record<string, unknown>): string | null {
  if (typeof ctx.before === 'string' && typeof ctx.after === 'string') {
    return `${ctx.before} → ${ctx.after}`;
  }
  if (typeof ctx.mode === 'string') {
    const n = typeof ctx.site_count === 'number' ? ctx.site_count : undefined;
    return ctx.mode === 'selected' && n !== undefined
      ? `selected — ${n} site${n === 1 ? '' : 's'}`
      : String(ctx.mode);
  }
  return null;
}

type ActivityState =
  | { kind: 'loading' }
  | { kind: 'ready'; items: readonly ActivityEntry[]; truncated: boolean }
  | { kind: 'error'; message: string };

/**
 * ADR-0149 — the expanded detail panel for one activity entry. Shows the full
 * audit record: exact time, parsed device, IP, in-infra geo, the log_id
 * reference, and the raw (decoded) context. All fields come from the activity
 * API response — no extra call. `stopPropagation` so selecting/copying inside
 * the panel doesn't collapse the card.
 */
function ActivityDetail({
  e,
  C,
}: {
  e: ActivityEntry;
  C: ReturnType<typeof useC>;
}): JSX.Element {
  const row = (k: string, v: string): JSX.Element => (
    <div key={k} style={{ display: 'flex', gap: 8, fontSize: 11, marginTop: 3 }}>
      <div style={{ fontFamily: monoFont, color: C.muted, width: 88, flexShrink: 0 }}>{k}</div>
      <div style={{ fontFamily: monoFont, color: C.text, wordBreak: 'break-all' }}>{v}</div>
    </div>
  );
  const rows: [string, string][] = [
    ['Action', e.action],
    ...(e.via_sill_support === true ? [['Made by', 'Sill support'] as [string, string]] : []),
    [
      'When',
      formatUtcDateTime(e.created_at),
    ],
    [
      'Device',
      e.device
        ? `${e.device.browser} · ${e.device.os}${e.device.device_type ? ` · ${e.device.device_type}` : ''}`
        : '—',
    ],
    ['IP address', e.actor_ip ?? '—'],
    ['Location', formatLocation(e.location) || '—'],
    ['Reference', e.log_id],
  ];
  const ctx = Object.entries(e.context ?? {});
  return (
    <div
      style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}
      onClick={(ev) => ev.stopPropagation()}
    >
      {rows.map(([k, v]) => row(k, v))}
      {ctx.length > 0 ? (
        <div style={{ marginTop: 6 }}>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 1,
              marginBottom: 2,
            }}
          >
            CONTEXT
          </div>
          {ctx.map(([k, v]) =>
            row(k, typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)),
          )}
        </div>
      ) : null}
    </div>
  );
}

function ActivityModal({
  member,
  onClose,
}: {
  member: UserWire | null;
  onClose: () => void;
}): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const toast = useToast();
  const [state, setState] = useState<ActivityState>({ kind: 'loading' });
  // ADR-0149 — which entry is expanded to its detail panel (by log_id).
  const [expanded, setExpanded] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // ADR-0152 Slice D — download the member's full trail as CSV. Uses the
  // blob path (credentialed fetch → the CORS-mounted export route) + the
  // shared download helper; the server audits the export (activity_exported).
  const onExport = useCallback(async (): Promise<void> => {
    if (http === null || member === null || http.requestBlob === undefined) return;
    setExporting(true);
    try {
      const blob = await http.requestBlob({
        method: 'GET',
        path: `/v1/users/${encodeURIComponent(member.user_id)}/activity/export?format=csv`,
      });
      downloadFile(`activity-${member.user_id}.csv`, await blob.text(), 'text/csv');
    } catch {
      toast({ tone: 'coral', title: "Couldn't export the activity log — try again." });
    } finally {
      setExporting(false);
    }
  }, [http, member, toast]);

  useEffect(() => {
    if (member === null || http === null) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    void (async () => {
      const r = await getUserActivity(http, member.user_id);
      if (cancelled) return;
      if (r.kind === 'ok')
        setState({ kind: 'ready', items: r.items, truncated: r.next_cursor !== null });
      else if (r.kind === 'forbidden')
        setState({ kind: 'error', message: 'Only an owner or admin can view activity.' });
      else if (r.kind === 'not_found')
        setState({ kind: 'error', message: 'This member no longer exists.' });
      else setState({ kind: 'error', message: r.message });
    })();
    return () => {
      cancelled = true;
    };
  }, [member, http]);

  return (
    <Modal
      open={member !== null}
      onClose={onClose}
      size="md"
      title="Activity"
      subtitle={member ? formatDisplayName(member.name) : ''}
    >
      <div className="p-6">
        {state.kind === 'loading' ? (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
            Loading…
          </div>
        ) : null}
        {state.kind === 'error' ? (
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, lineHeight: 1.5 }}>
            {state.message}
          </div>
        ) : null}
        {state.kind === 'ready' ? (
          state.items.length === 0 ? (
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              No recorded activity yet.
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <button
                  type="button"
                  onClick={() => void onExport()}
                  disabled={exporting}
                  style={{
                    fontFamily: monoFont,
                    fontSize: 10,
                    letterSpacing: 1,
                    color: C.text,
                    background: 'transparent',
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    padding: '4px 10px',
                    cursor: exporting ? 'default' : 'pointer',
                    opacity: exporting ? 0.5 : 1,
                  }}
                >
                  {exporting ? 'EXPORTING…' : 'EXPORT CSV'}
                </button>
              </div>
              {/* Previously fixed at 360px which cut the fifth row.
                  Size to content up to a sensible viewport-relative max
                  (~70% of the viewport, with a lower bound) so short
                  lists hug their rows and long lists still scroll. */}
              <div className="space-y-2" style={{ maxHeight: 'min(560px, 70vh)', overflowY: 'auto' }}>
              {state.items.map((e) => {
                const detail = detailForEntry(e.context);
                const isOpen = expanded === e.log_id;
                return (
                  <div
                    key={e.log_id}
                    className="p-3"
                    onClick={() => setExpanded(isOpen ? null : e.log_id)}
                    style={{
                      background: C.surface2,
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                      cursor: 'pointer',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 8,
                      }}
                    >
                      <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}>
                        {labelForAction(e.action)}
                        {e.via_sill_support === true ? (
                          <span
                            style={{
                              marginLeft: 8,
                              fontFamily: monoFont,
                              fontSize: 10,
                              letterSpacing: 1,
                              textTransform: 'uppercase',
                              color: C.amberFg ?? C.amber,
                              border: `1px solid ${C.amber}`,
                              borderRadius: 2,
                              padding: '1px 5px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            by Sill support
                          </span>
                        ) : null}
                      </div>
                      <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, flexShrink: 0 }}>
                        {isOpen ? '▾' : '▸'}
                      </div>
                    </div>
                    {detail !== null ? (
                      <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, marginTop: 2 }}>
                        {detail}
                      </div>
                    ) : null}
                    <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, marginTop: 4 }}>
                      {formatUtcDateTime(e.created_at)}
                      {e.device ? ` · ${e.device.browser} · ${e.device.os}` : ''}
                      {e.actor_ip ? ` · ${e.actor_ip}` : ''}
                      {formatLocation(e.location) ? ` · ${formatLocation(e.location)}` : ''}
                    </div>
                    {isOpen ? <ActivityDetail e={e} C={C} /> : null}
                  </div>
                );
              })}
              {state.truncated ? (
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 11,
                    color: C.muted,
                    textAlign: 'center',
                    paddingTop: 4,
                  }}
                >
                  Showing the 50 most recent — export for the full trail.
                </div>
              ) : null}
              </div>
            </>
          )
        ) : null}
      </div>
      <div
        className="px-6 py-4 flex justify-end"
        style={{ borderTop: `1px solid ${C.borderSoft ?? C.border}`, background: C.surface }}
      >
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2"
          style={{
            background: 'transparent',
            color: C.muted,
            border: `1px solid ${C.border}`,
            fontFamily: sansFont,
            fontSize: 13,
            borderRadius: 2,
          }}
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
